import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bot, CheckCircle2, Clock3, Eye, LoaderCircle, RefreshCw, Search, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { EmptyState, ErrorState, LoadingState } from "../../components/States";
import { StatusBadge } from "../../components/StatusBadge";
import { availabilityRequestsApi } from "../../services/availabilityRequestsApi";
import { robotApi } from "../../services/robotApi";
import type { AvailabilityJob, AvailabilityRequest } from "../../types";
import { formatDateTime } from "../../utils/format";
import { robotActive, robotPhaseTitle, useRobotOperation } from "./useRobotOperation";

type JobRow = AvailabilityJob & { request: AvailabilityRequest };
type RobotActionPayload =
  | { mode: "offerDraft"; requestId: string; label: string }
  | { mode: "statusCheck"; jobId: string; label: string };
type OperationNotice = {
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
};
const openStatuses = ["Odottaa lähetystä", "Lähetys käynnissä", "Tarjouspyyntö esitäytetty", "Kysytty Elisalta", "Odottaa vastausta", "Tarkistus käynnissä", "Virhe", "Vaatii manuaalisen tarkistuksen"];
const scheduledStatusCheckHoursUtc = [4, 10, 13];

function nextScheduledStatusCheckLabel() {
  const now = new Date();
  const next = scheduledStatusCheckHoursUtc
    .map((hour) => {
      const candidate = new Date(now);
      candidate.setUTCHours(hour, 0, 0, 0);
      if (candidate <= now) candidate.setUTCDate(candidate.getUTCDate() + 1);
      return candidate;
    })
    .sort((a, b) => a.getTime() - b.getTime())[0];
  return new Intl.DateTimeFormat("fi-FI", {
    timeZone: "Europe/Helsinki",
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(next);
}

function nextCheckText(job: JobRow) {
  if (robotActive(job)) return job.robotMessage || robotPhaseTitle(job);
  if (job.sendRetryAt) return `Uusi lähetysyritys: ${formatDateTime(job.sendRetryAt)}`;
  if (!job.elisaEventId) return "Odottaa Elisa-tunnistetta";
  if (job.status === "Tarkistus käynnissä") return "Käynnissä nyt";
  if (job.status === "Kysytty Elisalta" || job.status === "Odottaa vastausta") {
    return `Seuraava ajastettu ajo: ${nextScheduledStatusCheckLabel()}`;
  }
  return formatDateTime(job.nextCheckAt);
}

export function OpenRequestsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["requests"],
    queryFn: availabilityRequestsApi.list,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    staleTime: 0
  });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [speed, setSpeed] = useState("");
  const [priority, setPriority] = useState("");
  const [selected, setSelected] = useState<JobRow | null>(null);
  const [elisaEventIdInput, setElisaEventIdInput] = useState("");
  const [elisaEventIdError, setElisaEventIdError] = useState("");
  const [pendingAction, setPendingAction] = useState<RobotActionPayload | null>(null);
  const [notice, setNotice] = useState<OperationNotice | null>(null);
  const [operationId, setOperationId] = useState<string>();
  useRobotOperation(operationId, setNotice);
  const mutation = useMutation({
    mutationFn: (id: string) => availabilityRequestsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["results"] });
      queryClient.invalidateQueries({ queryKey: ["logs"] });
    }
  });
  const robotMutation = useMutation({
    mutationFn: async (payload: RobotActionPayload) => {
      if (payload.mode === "offerDraft") return robotApi.runOfferRobot(payload.requestId);
      return robotApi.runStatusCheckRobot(payload.jobId);
    },
    onMutate: (payload) => {
      setPendingAction(payload);
      setNotice({
        type: "info",
        title: "Robotti käynnissä",
        message: `${payload.label}: robotti kirjautuu Elisaan, lukee vahvistuskoodin ja suorittaa pyydetyn vaiheen.`
      });
    },
    onSuccess: (result, payload) => {
      setOperationId(result.operationId);
      const status = String(result?.status ?? "");
      setNotice({
        type: status === "authenticated" ? "success" : status === "queued" ? "info" : "warning",
        title: result.retryScheduled ? "Uusintayritys ajastettu" : status === "queued" ? "Robotti jonossa" : status === "skipped" ? "Ajoa ei käynnistetty" : status === "authenticated" ? "Robottiajo valmistui" : "Robottiajo ei valmistunut",
        message: result?.message || `${payload.label}: työ käsiteltiin onnistuneesti.`
      });
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["results"] });
      queryClient.invalidateQueries({ queryKey: ["logs"] });
    },
    onError: (error, payload) => {
      setNotice({
        type: "error",
        title: "Robottiajo epäonnistui",
        message: error instanceof Error ? `${payload.label}: ${error.message}` : `${payload.label}: tuntematon virhe.`
      });
    },
    onSettled: () => {
      setPendingAction(null);
    }
  });
  const eventIdMutation = useMutation({
    mutationFn: async ({ jobId, elisaEventId }: { jobId: string; elisaEventId: string | null }) =>
      availabilityRequestsApi.updateElisaEventId(jobId, elisaEventId),
    onSuccess: (updatedJob) => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["logs"] });
      setElisaEventIdError("");
      setSelected((current) => current && current.id === updatedJob.id
        ? { ...current, ...updatedJob }
        : current
      );
      setElisaEventIdInput(updatedJob.elisaEventId ?? "");
    }
  });
  useEffect(() => {
    setElisaEventIdInput(selected?.elisaEventId ?? "");
    setElisaEventIdError("");
  }, [selected?.id, selected?.elisaEventId]);
  const submitElisaEventId = (event: FormEvent) => {
    event.preventDefault();
    const value = elisaEventIdInput.trim().toUpperCase();
    if (value && !/^N\d{6,10}$/.test(value)) {
      setElisaEventIdError("Elisa-tunnisteen pitää olla muodossa N5067104.");
      return;
    }
    if (!selected) return;
    eventIdMutation.mutate({ jobId: selected.id, elisaEventId: value || null });
  };
  const jobs = useMemo(() => query.data?.flatMap((request) =>
    request.jobs.map((job) => ({ ...job, request }))
  ).filter((job) => openStatuses.includes(job.status))
    .filter((job) => !search || `${job.request.requestCode} ${job.request.streetAddress} ${job.request.city}`.toLowerCase().includes(search.toLowerCase()))
    .filter((job) => !status || job.status === status)
    .filter((job) => !speed || job.speed === speed)
    .filter((job) => !priority || job.request.priority === priority) ?? [], [query.data, search, status, speed, priority]);
  if (query.isLoading) return <LoadingState label="Ladataan avoimia kyselyitä…" />;
  if (query.isError) return <ErrorState />;
  const speedOptions = [...new Set(query.data!.flatMap((r) => r.jobs.map((j) => j.speed)))];

  return (
    <>
      <section className="page-heading"><div><span className="kicker">TYÖJONO</span><h1>Avoimet kyselyt</h1><p>Seuraa keskeneräisiä nopeuskohtaisia töitä ja käynnistä Elisa-robotin tarkistuksia.</p></div><span className="count-pill">{jobs.length} avointa työtä</span></section>
      <section className="card">
        <div className="filters">
          <label className="search-box"><Search size={18} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hae tunnuksella tai osoitteella" /></label>
          <select aria-label="Suodata statuksella" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Kaikki statukset</option>{openStatuses.map((item) => <option key={item}>{item}</option>)}</select>
          <select aria-label="Suodata nopeudella" value={speed} onChange={(e) => setSpeed(e.target.value)}><option value="">Kaikki nopeudet</option>{speedOptions.map((item) => <option key={item}>{item}</option>)}</select>
          <select aria-label="Suodata prioriteetilla" value={priority} onChange={(e) => setPriority(e.target.value)}><option value="">Kaikki prioriteetit</option><option value="normal">Normaali</option><option value="urgent">Kiireellinen</option></select>
        </div>
        {notice && <div className={`operation-notice operation-notice--${notice.type}`}>
          {notice.type === "success" ? <CheckCircle2 /> : notice.type === "warning" ? <AlertTriangle /> : notice.type === "error" ? <AlertTriangle /> : <Clock3 />}
          <div><strong>{notice.title}</strong><span>{notice.message}</span></div>
          {robotMutation.isPending && <LoaderCircle className="spin operation-notice__spinner" />}
        </div>}
        {!jobs.length ? <EmptyState title="Ei hakua vastaavia töitä" text="Muuta suodattimia tai luo uusi saatavuuskysely." /> :
        <div className="table-wrap"><table><thead><tr><th>Tunnus / kohde</th><th>Nopeus</th><th>Elisa-tunniste</th><th>Status</th><th>Tarkistukset</th><th>Prioriteetti</th><th className="align-right">Toiminnot</th></tr></thead>
          <tbody>{jobs.map((job) => {
            const isChecking = job.status === "Tarkistus käynnissä" || robotActive(job);
            const isThisPending = pendingAction?.mode === "statusCheck" && pendingAction.jobId === job.id;
            const statusCheckTitle = !job.elisaEventId
              ? "Tarkistus vaatii Elisa-tunnisteen"
              : isChecking
                ? "Tämän rivin tarkistus on jo käynnissä"
                : robotMutation.isPending
                  ? "Toinen robottiajo on parhaillaan käynnissä"
                  : "Tarkista tila Elisasta";
            return <tr key={job.id} className={isThisPending ? "row--active-operation" : undefined}>
            <td><strong>{job.request.requestCode}</strong><small>{job.request.streetAddress}, {job.request.postalCode} {job.request.city}</small></td>
            <td><span className="speed-chip">{job.speed}</span><small>{job.contractTerm}</small></td><td className="mono">{job.elisaEventId ?? "–"}</td><td><StatusBadge status={job.status} />{robotActive(job) && <small role="status">{robotPhaseTitle(job)}</small>}{job.errorMessage && !robotActive(job) && <small className="error-text">{job.errorMessage}</small>}</td>
            <td><small>Viimeisin: {formatDateTime(job.lastCheckedAt)}</small><small>{nextCheckText(job)}</small></td>
            <td>{job.request.priority === "urgent" ? <span className="priority priority--urgent">Kiireellinen</span> : "Normaali"}</td>
            <td><div className="row-actions">
              <button title="Näytä tiedot" onClick={() => setSelected(job)}><Eye /></button>
              {job.status === "Odottaa lähetystä" && !job.elisaEventId && <button title="Käynnistä Elisa-tarjouspyyntörobotti" disabled={robotMutation.isPending || robotActive(job)} onClick={() => robotMutation.mutate({ mode: "offerDraft", requestId: job.request.id, label: `${job.request.requestCode} / ${job.speed}` })}><Bot /></button>}
              <button title={statusCheckTitle} disabled={!job.elisaEventId || isChecking || robotMutation.isPending} onClick={() => robotMutation.mutate({ mode: "statusCheck", jobId: job.id, label: `${job.request.requestCode} / ${job.speed} / ${job.contractTerm}` })}><RefreshCw /></button>
              <button title="Poista kysely" disabled={mutation.isPending} onClick={() => window.confirm(`Poistetaanko kysely ${job.request.requestCode}?`) && mutation.mutate(job.request.id)}><Trash2 /></button>
            </div></td>
          </tr>})}</tbody>
        </table></div>}
      </section>
      {selected && <div className="modal-backdrop" onMouseDown={() => setSelected(null)}><aside className="detail-drawer" onMouseDown={(e) => e.stopPropagation()}>
        <div className="detail-drawer__header"><div><span className="kicker">TYÖN TIEDOT</span><h2>{selected.request.requestCode} · {selected.speed}</h2></div><button onClick={() => setSelected(null)}><X /></button></div>
        <StatusBadge status={selected.status} /><dl className="details">
          <div><dt>Kohde</dt><dd>{selected.request.streetAddress}, {selected.request.postalCode} {selected.request.city}</dd></div>
          <div><dt>Operaattori</dt><dd>{selected.request.operator}</dd></div><div><dt>Elisa-tunniste</dt><dd>{selected.elisaEventId ?? "Ei vielä tiedossa"}</dd></div>
          <div><dt>Sopimus</dt><dd>{selected.contractTerm}</dd></div><div><dt>Yhteyshenkilö / Efecte-tunnus</dt><dd>{selected.request.contactName}</dd></div>
          <div><dt>Sähköposti</dt><dd>{selected.email}</dd></div><div><dt>Luotu</dt><dd>{formatDateTime(selected.createdAt)}</dd></div>
          <div><dt>Lisätiedot</dt><dd>{selected.request.notes || "–"}</dd></div>
        </dl>
        <form className="manual-id-form" onSubmit={submitElisaEventId}>
          <label className="field">
            <span>Muokkaa Elisa-tunnistetta</span>
            <input
              value={elisaEventIdInput}
              onChange={(event) => {
                setElisaEventIdInput(event.target.value.toUpperCase());
                setElisaEventIdError("");
              }}
              placeholder="Esim. N5067104"
            />
            <small className="field-hint">Jos robotti ei löydä tunnistetta tai poimii väärän rivin, voit korjata sen käsin. Tyhjä arvo poistaa tunnisteen.</small>
            {elisaEventIdError && <small className="field-error">{elisaEventIdError}</small>}
            {eventIdMutation.isError && <small className="field-error">{eventIdMutation.error instanceof Error ? eventIdMutation.error.message : "Elisa-tunnisteen tallennus epäonnistui."}</small>}
          </label>
          <button className="button button--secondary" type="submit" disabled={eventIdMutation.isPending}>
            {eventIdMutation.isPending ? "Tallennetaan…" : "Tallenna tunniste"}
          </button>
        </form>
      </aside></div>}
    </>
  );
}
